import { useState, useEffect, useRef } from 'react'
import { apiFetch } from '../services/api'
import { useAuth } from '../context/AuthContext'
import { useMoneda } from '../context/MonedaContext'
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts'
import { toPng } from 'html-to-image'

const PERIODOS = [
  { label: 'Hoy',           value: 'hoy' },
  { label: 'Semana',        value: 'semana' },
  { label: 'Mes',           value: 'mes' },
  { label: 'Año',           value: 'anio' },
  { label: 'Personalizado', value: 'custom' },
]

const COLORES = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6']

function getRangoFechas(periodo, custom) {
  const ahora = new Date()
  let desde, hasta

  if (periodo === 'hoy') {
    desde = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate())
    hasta = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate(), 23, 59, 59)
  } else if (periodo === 'semana') {
    const dia = ahora.getDay()
    const lunes = new Date(ahora)
    lunes.setDate(ahora.getDate() - (dia === 0 ? 6 : dia - 1))
    lunes.setHours(0, 0, 0, 0)
    desde = lunes
    hasta = new Date()
  } else if (periodo === 'mes') {
    desde = new Date(ahora.getFullYear(), ahora.getMonth(), 1)
    hasta = new Date()
  } else if (periodo === 'anio') {
    desde = new Date(ahora.getFullYear(), 0, 1)
    hasta = new Date()
  } else if (periodo === 'custom' && custom.desde && custom.hasta) {
    desde = new Date(custom.desde + 'T00:00:00')
    hasta = new Date(custom.hasta + 'T23:59:59')
  } else {
    desde = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate())
    hasta = new Date()
  }

  return { desde: desde.toISOString(), hasta: hasta.toISOString() }
}

function GraficoCard({ titulo, children, refGrafico }) {
  const exportar = async () => {
    if (!refGrafico.current) return
    try {
      const dataUrl = await toPng(refGrafico.current, { cacheBust: true })
      const link = document.createElement('a')
      link.download = `${titulo.replace(/\s+/g, '_')}.png`
      link.href = dataUrl
      link.click()
    } catch (err) {
      console.error('Error exportando gráfico:', err)
    }
  }

  return (
    <div className="card" style={{ marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--text, #0f172a)' }}>{titulo}</h2>
        <button className="btn btn-outline" onClick={exportar}
          style={{ padding: '0.4rem 0.75rem', fontSize: '0.8rem' }}>
          Exportar PNG
        </button>
      </div>
      <div ref={refGrafico} style={{ background: 'white', padding: '0.5rem' }}>
        <div style={{ fontSize: '0.65rem', color: '#cbd5e1', textAlign: 'right', marginBottom: '0.25rem' }}>
          Inventario PyME — puntum.cl
        </div>
        {children}
      </div>
    </div>
  )
}

export default function Reportes() {
  const { perfil } = useAuth()
  const { formatearMoneda } = useMoneda()
  const [periodo, setPeriodo] = useState('mes')
  const [customFechas, setCustomFechas] = useState({ desde: '', hasta: '' })
  const [loading, setLoading] = useState(true)

  const [ventasPorDia, setVentasPorDia] = useState([])
  const [topProductos, setTopProductos] = useState([])
  const [ventasPorMetodo, setVentasPorMetodo] = useState([])
  const [ventasPorVendedor, setVentasPorVendedor] = useState([])

  const refVentasDia    = useRef(null)
  const refTopProductos = useRef(null)
  const refMetodoPago   = useRef(null)
  const refVendedores   = useRef(null)

  useEffect(() => {
    cargarDatos()
  }, [periodo, customFechas])

  const cargarDatos = async () => {
    if (periodo === 'custom' && (!customFechas.desde || !customFechas.hasta)) return
    setLoading(true)
    try {
      const { desde, hasta } = getRangoFechas(periodo, customFechas)
      const res = await apiFetch(`/reportes/ventas?desde=${desde}&hasta=${hasta}`)

      if (res && res.ventasPorDia) {
        setVentasPorDia(res.ventasPorDia || [])
        setTopProductos(res.topProductos || [])
        setVentasPorMetodo(res.ventasPorMetodo || [])
        setVentasPorVendedor(res.ventasPorVendedor || [])
      } else {
        setVentasPorDia([])
        setTopProductos([])
        setVentasPorMetodo([])
        setVentasPorVendedor([])
      }
    } catch (err) {
      console.warn('No se pudieron cargar los reportes del servidor:', err)
      setVentasPorDia([])
      setTopProductos([])
      setVentasPorMetodo([])
      setVentasPorVendedor([])
    } finally {
      setLoading(false)
    }
  }

  const sinDatos = (arr) => !arr || arr.length === 0

  const MensajeVacio = () => (
    <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-light, #64748b)', fontSize: '0.875rem' }}>
      Sin datos para el período seleccionado
    </div>
  )

  return (
    <div className="main-content">
      <div className="page-header">
        <h1 className="page-title">Reportes</h1>
        <p className="page-subtitle">Análisis visual de ventas por período</p>
      </div>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {PERIODOS.map(p => (
            <button key={p.value}
              className={`btn ${periodo === p.value ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setPeriodo(p.value)}
              style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}>
              {p.label}
            </button>
          ))}
          {periodo === 'custom' && (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginLeft: '0.5rem' }}>
              <input type="date" className="form-input" style={{ width: 'auto' }}
                value={customFechas.desde}
                onChange={e => setCustomFechas(f => ({ ...f, desde: e.target.value }))} />
              <span style={{ color: 'var(--text-light, #64748b)' }}>—</span>
              <input type="date" className="form-input" style={{ width: 'auto' }}
                value={customFechas.hasta}
                onChange={e => setCustomFechas(f => ({ ...f, hasta: e.target.value }))} />
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-light, #64748b)' }}>Cargando reportes...</div>
      ) : (
        <>
          <GraficoCard titulo="Ventas por día" refGrafico={refVentasDia}>
            {sinDatos(ventasPorDia) ? <MensajeVacio /> : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={ventasPorDia}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="fecha" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={v => formatearMoneda(v)} width={90} />
                  <Tooltip formatter={v => formatearMoneda(v)} />
                  <Line type="monotone" dataKey="total" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} name="Total" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </GraficoCard>

          <GraficoCard titulo="Top 5 productos más vendidos (por unidades)" refGrafico={refTopProductos}>
            {sinDatos(topProductos) ? <MensajeVacio /> : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={topProductos} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="producto" tick={{ fontSize: 12 }} width={140} />
                  <Tooltip />
                  <Bar dataKey="cantidad" fill="#10b981" name="Unidades vendidas" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </GraficoCard>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <GraficoCard titulo="Ventas por método de pago" refGrafico={refMetodoPago}>
              {sinDatos(ventasPorMetodo) ? <MensajeVacio /> : (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={ventasPorMetodo}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="45%"
                      outerRadius={90}
                      label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                      labelLine={true}
                    >
                      {ventasPorMetodo.map((_, i) => (
                        <Cell key={i} fill={COLORES[i % COLORES.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={v => formatearMoneda(v)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </GraficoCard>

            <GraficoCard titulo="Ventas por vendedor" refGrafico={refVendedores}>
              {sinDatos(ventasPorVendedor) ? <MensajeVacio /> : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={ventasPorVendedor}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="vendedor" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={v => formatearMoneda(v)} width={90} />
                    <Tooltip formatter={v => formatearMoneda(v)} />
                    <Bar dataKey="total" name="Total vendido" radius={[4, 4, 0, 0]}>
                      {ventasPorVendedor.map((_, i) => (
                        <Cell key={i} fill={COLORES[i % COLORES.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </GraficoCard>
          </div>
        </>
      )}
    </div>
  )
}